import React, { useCallback, useEffect, useMemo, useState } from "react";
import { http } from "../../../services/HttpService";
import { HTTP_ENDPOINTS } from "../../../consts/api";
import { uiNotification } from "../../../services/UINotificationService";
import styles from "./ProcedureWellSyncPrescriptions.module.scss";

const SHIPPING_OPTIONS = [
  { value: "SHIPPING_METHOD_3DAY", label: "3-Day" },
  { value: "SHIPPING_METHOD_2DAY", label: "2-Day" },
  { value: "SHIPPING_METHOD_OVERNIGHT", label: "Overnight" },
  { value: "SHIPPING_METHOD_NON_COLD_2DAY", label: "2-Day (Non-Cold)" },
];

const SHIPPING_LABEL = {
  SHIPPING_METHOD_3DAY: "3-Day",
  SHIPPING_METHOD_2DAY: "2-Day",
  SHIPPING_METHOD_OVERNIGHT: "Overnight",
  SHIPPING_METHOD_NON_COLD_2DAY: "2-Day (Non-Cold)",
};

function formatOrderStatus(status) {
  if (!status) return "";
  return status
    .replace(/^ORDER_STATUS_/, "")
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

function normalizePhone(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return (phone || "").trim();
}

function normalizeUsState(state) {
  const v = (state || "").trim();
  if (!v) return "";
  if (v.length === 2) return v.toUpperCase();
  return v.slice(0, 2).toUpperCase();
}

function toIsoDob(dob) {
  if (!dob) return "";
  if (String(dob).includes("T")) return dob;
  return `${dob}T00:00:00Z`;
}

function normalizeCountry(c) {
  const u = (c || "").trim().toUpperCase();
  if (u === "US" || u === "USA" || u === "UNITED STATES") return "US";
  return u || "US";
}

/**
 * WellSync CareConnect ordering UI (procedure prescription context).
 * procedurePrescriptionInformationId is optional; the backend creates/links one on order submit when procedureId is sent.
 */
export function ProcedureWellSyncPrescriptions({
  clinicId,
  procedureId,
  procedurePrescriptionInformationId,
  clientId,
  onBack,
  patient,
  prescriber,
  prescriptionLines,
}) {
  const [drugs, setDrugs] = useState([]);
  const [drugsLoading, setDrugsLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [cart, setCart] = useState([]);
  const [shippingMethod, setShippingMethod] = useState("SHIPPING_METHOD_3DAY");
  const [prices, setPrices] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDrug, setSheetDrug] = useState(null);
  const [sheetDosageId, setSheetDosageId] = useState("");
  const [sheetQty, setSheetQty] = useState(1);
  const [sheetDays, setSheetDays] = useState(30);
  const [sheetRefills, setSheetRefills] = useState(0);
  const [sheetDirections, setSheetDirections] = useState("");
  const [sheetJustification, setSheetJustification] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const patientState = useMemo(
    () => normalizeUsState(patient?.state),
    [patient?.state],
  );

  const loadDrugs = useCallback(async () => {
    setDrugsLoading(true);
    try {
      const res = await http.post(HTTP_ENDPOINTS.wellsync.drugs(), {
        clinicId,
        patientState: patientState || undefined,
      });
      const payload = res.data && res.data.data;
      const rawDrugs = payload && payload.drugs;
      const list = rawDrugs != null ? rawDrugs : [];
      setDrugs(Array.isArray(list) ? list : []);
    } catch (e) {
      uiNotification.error(
        e.response?.data?.message || "Failed to load WellSync catalog",
      );
    } finally {
      setDrugsLoading(false);
    }
  }, [clinicId, patientState]);

  useEffect(() => {
    void loadDrugs();
  }, [loadDrugs]);

  const loadPrices = useCallback(async () => {
    try {
      const res = await http.post(HTTP_ENDPOINTS.wellsync.prices(), {
        clinicId,
      });
      const priceData = res.data && res.data.data;
      setPrices(priceData != null ? priceData : null);
    } catch (e) {
      setPrices(null);
    }
  }, [clinicId]);

  useEffect(() => {
    void loadPrices();
  }, [loadPrices]);

  const filteredDrugs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => (d.name || "").toLowerCase().includes(q));
  }, [drugs, filter]);

  const openSheet = (drug) => {
    setSheetDrug(drug);
    const first = drug.dosages?.[0];
    setSheetDosageId(first?.id || "");
    setSheetQty(parseInt(first?.defaultQuantity, 10) || 1);
    setSheetRefills(0);
    setSheetDirections(first?.directions || "");
    setSheetJustification("");
    setSheetOpen(true);
  };

  const selectedDosage = useMemo(() => {
    if (!sheetDrug?.dosages) return null;
    return sheetDrug.dosages.find((d) => d.id === sheetDosageId) || null;
  }, [sheetDrug, sheetDosageId]);

  const addToCart = () => {
    if (!sheetDrug || !selectedDosage) {
      uiNotification.error("Select a dosage");
      return;
    }
    if (!sheetDirections.trim()) {
      uiNotification.error("Directions are required");
      return;
    }
    const line = {
      localId: `${Date.now()}-${Math.random()}`,
      drugId: sheetDrug.id,
      dosageId: selectedDosage.id,
      drugName: sheetDrug.name,
      dosage: selectedDosage.concentration,
      quantity: sheetQty,
      daysSupply: sheetDays,
      refills: sheetRefills,
      directions: sheetDirections.trim(),
      justification: sheetJustification.trim(),
    };
    setCart((c) => [...c, line]);
    setSheetOpen(false);
  };

  const removeLine = (localId) => {
    setCart((c) => c.filter((x) => x.localId !== localId));
  };

  const estimatedTotal = useMemo(() => {
    if (!prices?.drugPrices || !cart.length) return null;
    let sum = 0;
    let ok = true;
    cart.forEach((item) => {
      const dp = prices.drugPrices[item.drugId];
      if (!dp || dp[item.dosageId] == null) ok = false;
      else sum += Number(dp[item.dosageId]) || 0;
    });
    if (!ok) return null;
    const ship = prices.shippingFees?.[shippingMethod];
    if (ship != null) sum += Number(ship) || 0;
    return sum;
  }, [cart, prices, shippingMethod]);

  const orderHistory = useMemo(() => {
    const lines = Array.isArray(prescriptionLines) ? prescriptionLines : [];
    const wsLines = lines.filter((l) => l.wellsync_order_id);
    const grouped = {};
    wsLines.forEach((l) => {
      const key = l.wellsync_order_id;
      if (!grouped[key]) {
        grouped[key] = {
          orderId: key,
          status: l.wellsync_order_status || "",
          shipping: l.wellsync_shipping_method || "",
          date: l.created || l.modified || "",
          drugs: [],
        };
      }
      grouped[key].drugs.push(l.medicine_name || l.wellsync_drug_id || "");
    });
    return Object.values(grouped).sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
  }, [prescriptionLines]);

  const [trackingOrderId, setTrackingOrderId] = useState(null);

  const trackOrder = async (orderId) => {
    setTrackingOrderId(orderId);
    try {
      const res = await http.post(HTTP_ENDPOINTS.wellsync.ordersTrack(), {
        clinicId,
        orderId,
      });
      const items = res.data?.data?.prescriptionItems;
      if (Array.isArray(items) && items.length > 0) {
        const newStatus = items[0].status || items[0].orderStatus;
        if (newStatus) {
          uiNotification.success("Status: " + formatOrderStatus(newStatus));
        } else {
          uiNotification.success("Tracking data received");
        }
      } else {
        uiNotification.info("No tracking updates available");
      }
    } catch (e) {
      uiNotification.error(
        e.response?.data?.message || "Failed to track order",
      );
    } finally {
      setTrackingOrderId(null);
    }
  };

  const handleSubmit = async () => {
    if (!patient?.firstName || !patient?.lastName) {
      uiNotification.error("Patient name is required");
      return;
    }
    if (!patientState || patientState.length !== 2) {
      uiNotification.error("Patient state must be a valid 2-letter code");
      return;
    }
    if (!prescriber?.npi || !/^\d{10}$/.test(String(prescriber.npi).trim())) {
      uiNotification.error("A valid 10-digit prescriber NPI is required");
      return;
    }
    if (!prescriber?.phone?.trim()) {
      uiNotification.error("Prescriber phone is required");
      return;
    }
    if (!cart.length) {
      uiNotification.error("Add at least one medication");
      return;
    }

    setSubmitting(true);
    try {
      const externalCaseId = `ar-ws-${procedureId}-${Date.now()}`;
      const orderRes = await http.post(HTTP_ENDPOINTS.wellsync.orders(), {
        clinicId,
        procedureId,
        externalCaseId,
        patient: {
          externalId: String(clientId),
          firstName: patient.firstName,
          lastName: patient.lastName,
          dateOfBirth: toIsoDob(patient.dob),
          email: patient.email || "",
          phone: normalizePhone(patient.phone),
          primaryAddress: {
            addressLineOne: patient.address || "",
            city: patient.city || "",
            administrativeArea: patientState,
            zip: patient.zip || "",
            country: normalizeCountry(patient.country),
          },
        },
        clinician: {
          externalId: String(prescriber.externalId || "wellsync-clinician"),
          npi: String(prescriber.npi).trim(),
          firstName: prescriber.firstName || "",
          lastName: prescriber.lastName || "",
          phone: normalizePhone(prescriber.phone),
          email: prescriber.email || "",
        },
        shippingMethod,
        shippingAddress: {
          addressLineOne: patient.address || "",
          city: patient.city || "",
          administrativeArea: patientState,
          zip: patient.zip || "",
          country: normalizeCountry(patient.country),
        },
        prescriptionItems: cart.map((item) => ({
          drugId: item.drugId,
          dosageId: item.dosageId,
          quantity: item.quantity,
          directions: item.directions,
          daysSupply: item.daysSupply,
          totalRefills: item.refills,
          justification: item.justification,
        })),
      });

      const orderPayload = orderRes.data?.data;
      const orderId = orderPayload?.orderId;
      const status = orderPayload?.status;
      const rawPrescriptionInfoId =
        orderPayload?.procedurePrescriptionInformationId;
      const prescriptionInfoId =
        rawPrescriptionInfoId != null
          ? rawPrescriptionInfoId
          : procedurePrescriptionInformationId != null
          ? procedurePrescriptionInformationId
          : 0;

      await http.post(HTTP_ENDPOINTS.wellsync.prescriptionLinesCreate(), {
        clinicId,
        procedureId,
        procedurePrescriptionInformationId: prescriptionInfoId,
        lines: cart.map((item) => ({
          medicine_name: item.drugName,
          dosage: item.dosage,
          quantity: String(item.quantity),
          refills: String(item.refills),
          wellsync_drug_id: item.drugId,
          wellsync_shipping_method: shippingMethod,
          wellsync_order_id: orderId,
          wellsync_order_status: status,
        })),
      });

      uiNotification.success("Order submitted to WellSync");
      setCart([]);
    } catch (e) {
      uiNotification.error(
        e.response?.data?.message || e.message || "Order failed",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <button type="button" className={styles.btnSecondary} onClick={onBack}>
          Back
        </button>
        <span className={styles.muted}>
          WellSync catalog (clinic {clinicId})
        </span>
      </div>

      <div className={styles.grid}>
        <div className={styles.panel}>
          <div className={styles.toolbar}>
            <input
              className={styles.search}
              placeholder="Search drugs..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => void loadDrugs()}
              disabled={drugsLoading}
            >
              Refresh
            </button>
          </div>
          {drugsLoading ? (
            <p className={styles.muted}>Loading catalog…</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Drug</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredDrugs.slice(0, 50).map((d) => (
                  <tr key={d.id}>
                    <td>{d.name}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.btn}
                        onClick={() => openSheet(d)}
                      >
                        Add
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {filteredDrugs.length > 50 ? (
            <p className={styles.muted}>
              Showing first 50 matches — refine search.
            </p>
          ) : null}
        </div>

        <div className={styles.panel}>
          <h4>Cart ({cart.length})</h4>
          <div className={styles.field}>
            <label>Shipping</label>
            <select
              className={styles.select}
              value={shippingMethod}
              onChange={(e) => setShippingMethod(e.target.value)}
            >
              {SHIPPING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Medication</th>
                <th>Qty</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {cart.map((line) => (
                <tr key={line.localId}>
                  <td>
                    {line.drugName} — {line.dosage}
                  </td>
                  <td>{line.quantity}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => removeLine(line.localId)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {estimatedTotal != null ? (
            <p>
              <strong>Estimated total:</strong> ${estimatedTotal.toFixed(2)}
            </p>
          ) : (
            <p className={styles.muted}>
              Pricing may be unavailable until the catalog and fee schedule
              load.
            </p>
          )}
          <button
            type="button"
            className={styles.btn}
            onClick={() => void handleSubmit()}
            disabled={submitting || !cart.length}
          >
            {submitting ? "Sending…" : "Submit to WellSync"}
          </button>
        </div>
      </div>

      {orderHistory.length > 0 && (
        <div className={styles.historySection}>
          <h4 className={styles.historyTitle}>ORDER HISTORY</h4>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>PRESCRIPTION</th>
                <th>DRUGS</th>
                <th>FULFILLMENT</th>
                <th>STATUS</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orderHistory.map((order) => (
                <tr
                  key={order.orderId}
                  className={styles.historyRow}
                >
                  <td>
                    <div>{formatDate(order.date)}</div>
                    <div className={styles.orderId}>{order.orderId}</div>
                  </td>
                  <td>{order.drugs.join(", ")}</td>
                  <td>{SHIPPING_LABEL[order.shipping] || order.shipping}</td>
                  <td>{formatOrderStatus(order.status)}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnIcon}
                      title="Refresh status"
                      disabled={trackingOrderId === order.orderId}
                      onClick={() => void trackOrder(order.orderId)}
                    >
                      &#x21BB;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className={styles.muted}>Click a row to view prescription details.</p>
        </div>
      )}

      {sheetOpen && sheetDrug ? (
        <div className={styles.sheet}>
          <div className={styles.sheetInner}>
            <h4>{sheetDrug.name}</h4>
            <div className={styles.field}>
              <label>Dosage</label>
              <select
                className={styles.input}
                value={sheetDosageId}
                onChange={(e) => {
                  setSheetDosageId(e.target.value);
                  const d = sheetDrug.dosages?.find(
                    (x) => x.id === e.target.value,
                  );
                  if (d) {
                    setSheetQty(parseInt(d.defaultQuantity, 10) || 1);
                    setSheetDirections(d.directions || "");
                  }
                }}
              >
                {(sheetDrug.dosages || []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.concentration}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label>Quantity</label>
              <input
                className={styles.input}
                type="text"
                inputMode="numeric"
                value={sheetQty}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setSheetQty(v === "" ? "" : Number(v));
                }}
                onBlur={() => { if (!sheetQty) setSheetQty(1); }}
              />
            </div>
            <div className={styles.field}>
              <label>Days supply</label>
              <input
                className={styles.input}
                type="text"
                inputMode="numeric"
                value={sheetDays}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setSheetDays(v === "" ? "" : Number(v));
                }}
                onBlur={() => { if (!sheetDays) setSheetDays(1); }}
              />
            </div>
            <div className={styles.field}>
              <label>Refills</label>
              <input
                className={styles.input}
                type="text"
                inputMode="numeric"
                value={sheetRefills}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setSheetRefills(v === "" ? "" : Number(v));
                }}
                onBlur={() => { if (sheetRefills === "" || sheetRefills === null) setSheetRefills(0); }}
              />
            </div>
            <div className={styles.field}>
              <label>Directions</label>
              <textarea
                className={styles.input}
                rows={3}
                value={sheetDirections}
                onChange={(e) => setSheetDirections(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label>Justification (optional)</label>
              <textarea
                className={styles.input}
                rows={2}
                value={sheetJustification}
                onChange={(e) => setSheetJustification(e.target.value)}
              />
            </div>
            <div className={styles.toolbar}>
              <button type="button" className={styles.btn} onClick={addToCart}>
                Add to cart
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setSheetOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProcedureWellSyncPrescriptions;
